import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { toLocalInputValue } from '../../utils/datetime.js';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { FormField, Switch } from '../../components/FormField.jsx';
import { EmptyState } from './Teams.jsx';

const empty = {
  title: '',
  date: '',
  location: '',
  agenda: '',
  minutesBody: '',
  minutesUrl: '',
  published: true,
};

export default function Meetings() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function refresh() {
    const r = await Admin.meetings.list();
    setItems(r.meetings);
  }
  useEffect(() => { refresh(); }, []);

  function openCreate() {
    setForm(empty);
    setErr(null);
    setModal('create');
  }
  function openEdit(m) {
    setForm({
      title: m.title,
      date: m.date ? toLocalInputValue(m.date) : '',
      location: m.location || '',
      agenda: m.agenda || '',
      minutesBody: m.minutesBody || '',
      minutesUrl: m.minutesUrl || '',
      published: !!m.published,
    });
    setErr(null);
    setModal({ id: m.id });
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        title: form.title,
        date: new Date(form.date).toISOString(),
        location: form.location || undefined,
        agenda: form.agenda || undefined,
        minutesBody: form.minutesBody || undefined,
        minutesUrl: form.minutesUrl || undefined,
        published: form.published,
      };
      if (modal === 'create') await Admin.meetings.create(payload);
      else await Admin.meetings.update(modal.id, payload);
      setModal(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  async function remove(id) {
    await Admin.meetings.remove(id);
    await refresh();
  }

  const now = Date.now();
  const upcoming = items.filter((m) => new Date(m.date).getTime() >= now);
  const past = items.filter((m) => new Date(m.date).getTime() < now);

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Meetings & minutes"
        description="Board meetings with agendas and minutes. Published entries appear on the public site."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New meeting</button>}
      />

      {items.length === 0 && <EmptyState onAction={openCreate} label="Add a meeting" />}

      {upcoming.length > 0 && (
        <section className="mb-8">
          <div className="eyebrow mb-3">Upcoming</div>
          <div className="space-y-3">
            {upcoming.map((m) => <MeetingCard key={m.id} m={m} onEdit={openEdit} onDelete={setConfirm} />)}
          </div>
        </section>
      )}

      {past.length > 0 && (
        <section>
          <div className="eyebrow mb-3">Past</div>
          <div className="space-y-3">
            {past.map((m) => <MeetingCard key={m.id} m={m} onEdit={openEdit} onDelete={setConfirm} />)}
          </div>
        </section>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New meeting' : 'Edit'}
        title={modal === 'create' ? 'Add a meeting' : form.title || 'Edit'}
        size="lg"
        footer={
          <>
            <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('meeting-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <form id="meeting-form" onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Title" required>
              <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required autoFocus />
            </FormField>
            <FormField label="Date & time" required>
              <input type="datetime-local" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
            </FormField>
          </div>
          <FormField label="Location">
            <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Clubhouse, Zoom, etc." />
          </FormField>
          <FormField label="Agenda" hint="Shown before the meeting.">
            <textarea className="input" rows={4} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} placeholder="Plain text or simple HTML" />
          </FormField>
          <FormField label="Minutes (HTML)" hint="Shown after the meeting.">
            <textarea className="input font-mono text-xs" rows={10} value={form.minutesBody} onChange={(e) => setForm({ ...form, minutesBody: e.target.value })} placeholder="<p>Call to order at 7:00 PM…</p>" />
          </FormField>
          <FormField label="Minutes PDF / document URL (optional)" hint="Link to an external document if preferred.">
            <input className="input" value={form.minutesUrl} onChange={(e) => setForm({ ...form, minutesUrl: e.target.value })} />
          </FormField>
          <div className="pt-2 border-t border-line">
            <Switch
              checked={form.published}
              onChange={(v) => setForm({ ...form, published: v })}
              label="Published"
              hint="Off = hidden from the public site."
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete "${confirm?.title}"?`}
        message="Deletes the meeting record. Unpublish instead to just hide it."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

function MeetingCard({ m, onEdit, onDelete }) {
  const d = new Date(m.date);
  return (
    <article className="card">
      <div className="flex gap-4 items-start">
        <div className="text-center shrink-0 w-16 pt-1">
          <div className="numeric text-2xl text-ink leading-none">{d.getDate()}</div>
          <div className="eyebrow mt-1">{d.toLocaleDateString(undefined, { month: 'short' })}</div>
          <div className="text-[10px] text-ink-3 mt-0.5">{d.getFullYear()}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-display text-xl">{m.title}</h3>
            {!m.published && <span className="chip-neutral">Draft</span>}
          </div>
          <div className="text-xs text-ink-3">
            {d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            {m.location ? ` · ${m.location}` : ''}
          </div>
          {m.agenda && <p className="text-sm text-ink-2 mt-2 line-clamp-2 whitespace-pre-wrap">{m.agenda}</p>}
          <div className="flex gap-3 mt-3 text-xs">
            {m.minutesBody && <span className="text-success">● Minutes inline</span>}
            {m.minutesUrl && <a href={m.minutesUrl} target="_blank" rel="noreferrer" className="text-brand underline underline-offset-4">Minutes document →</a>}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button className="btn-outline btn-xs" onClick={() => onEdit(m)}>Edit</button>
          <button className="btn-ghost btn-xs text-danger" onClick={() => onDelete(m)}>Delete</button>
        </div>
      </div>
    </article>
  );
}
